import { useEffect, useMemo, useRef } from 'react';

/**
 * Returns a debounced version of `callback` that delays invocation until
 * `delayMs` have elapsed since the last call. The latest callback is always
 * invoked (kept in a ref), so the debounced function identity is stable and
 * safe to pass to children. Pending calls are cancelled on unmount.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs: number,
): ((...args: TArgs) => void) & { cancel: () => void } {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest callback without changing the debounced identity.
  useEffect(() => {
    callbackRef.current = callback;
  });

  // Cancel any pending invocation on unmount.
  useEffect(() => () => clearPendingTimer(timerRef), []);

  return useMemo(() => {
    const debounced = (...args: TArgs): void => {
      clearPendingTimer(timerRef);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        callbackRef.current(...args);
      }, delayMs);
    };
    debounced.cancel = (): void => clearPendingTimer(timerRef);
    return debounced;
  }, [delayMs]);
}

function clearPendingTimer(
  timerRef: React.RefObject<ReturnType<typeof setTimeout> | null>,
): void {
  if (timerRef.current !== null) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}
